import { useCallback, useEffect, useMemo, useState } from "react";

const CAMERA_DEVICE_STORAGE_KEY = "selectedCameraDeviceId";

/**
 * Shared camera-selection hook.
 *
 * Handles:
 * - Persisting / restoring the selected device via localStorage
 * - Auto-selecting the best available camera (non-wide back camera → any back camera → first available)
 * - Cleaning up a stale stored device when it is no longer present
 * - Filtering wide / ultra-wide cameras out of the visible options list
 *
 * Each consuming component is responsible for assembling its own `videoConstraints`
 * from the returned values, adding any component-specific constraints (e.g. `focusMode`)
 * on top.
 */
export const useCameraDevice = () => {
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(() => {
        if (typeof localStorage === "undefined") return null;
        return localStorage.getItem(CAMERA_DEVICE_STORAGE_KEY);
    });
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [torchEnabled, setTorchEnabled] = useState(false);

    const toggleTorch = useCallback(() => setTorchEnabled((v) => !v), []);

    const selectedDeviceIdExists = devices.some((d) => d.deviceId === selectedDeviceId);

    // Back cameras that are not wide / ultra-wide — used for auto-selection priority
    const filteredDevices = useMemo(
        () => devices.filter((d) => {
            const label = d.label.toLowerCase();
            if (label.includes("wide") || label.includes("ultra") || label.includes("0.5")) return false;
            return label.includes("back") || label.includes("rear");
        }),
        [devices]
    );

    // Auto-select when the device list first loads, but never override a valid stored preference
    useEffect(() => {
        if (!devices.length) return;
        if (selectedDeviceIdExists) return;

        const preferred = filteredDevices[0]
            || devices.find((d) => d.label.toLowerCase().includes("back"))
            || devices[0];

        if (preferred) setSelectedDeviceId(preferred.deviceId);
    }, [devices, filteredDevices, selectedDeviceIdExists]);

    /** Manual selection — updates state and persists to localStorage */
    const setCameraDevice = useCallback((_field: string, value: string | null) => {
        if (value) {
            setSelectedDeviceId(value);
            localStorage.setItem(CAMERA_DEVICE_STORAGE_KEY, value);
        }
    }, []);

    /**
     * Call this with the enumerated video inputs once available (e.g. inside
     * `onUserMedia`).  Updates the device list and removes a stale stored device
     * when it no longer appears in the list.
     */
    const onDevicesLoaded = useCallback((videoInputs: MediaDeviceInfo[]) => {
        setDevices(videoInputs);
        setSelectedDeviceId((prev) => {
            if (!prev) return prev;
            const exists = videoInputs.some((d) => d.deviceId === prev);
            if (!exists) {
                localStorage.removeItem(CAMERA_DEVICE_STORAGE_KEY);
                return null;
            }
            return prev;
        });
    }, []);

    /**
     * Drop-in `onUserMedia` handler for components that only need device
     * enumeration (no extra capability reading).
     */
    const onUserMedia = useCallback(() => {
        navigator.mediaDevices
            .enumerateDevices()
            .then((mediaDevices) => {
                onDevicesLoaded(mediaDevices.filter((d) => d.kind === "videoinput"));
            })
            .catch(() => setDevices([]));
    }, [onDevicesLoaded]);

    /** Wide / ultra-wide cameras excluded — ready to pass to a Select component */
    const visibleCameraOptions = useMemo(
        () => devices
            .filter((d) => {
                const l = d.label.toLowerCase();
                return !l.includes("wide") && !l.includes("ultra") && !l.includes("0.5");
            })
            .map((d, i) => ({ label: d.label || `Camera ${i + 1}`, value: d.deviceId })),
        [devices]
    );

    return {
        devices,
        selectedDeviceId,
        selectedDeviceIdExists,
        torchEnabled,
        toggleTorch,
        setCameraDevice,
        onDevicesLoaded,
        onUserMedia,
        visibleCameraOptions,
    };
};
